import {
  Box,
  Button,
  Paper,
  Typography,
  styled,
  useTheme,
  Fade,
  IconButton,
} from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { CircularProgressWithLabel } from './loadings/CircularProgressWithLabel'
import CloseIcon from '@mui/icons-material/Close'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import ImageIcon from '@mui/icons-material/Image'
import DescriptionIcon from '@mui/icons-material/Description'
import TableChartIcon from '@mui/icons-material/TableChart'
import SlideshowIcon from '@mui/icons-material/Slideshow'
import { FileTypeEnum } from '../common/interfaces'

const FilePreview = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  width: '100%',
  transition: theme.transitions.create(['box-shadow']),
  '&:hover': {
    boxShadow: theme.shadows[2],
  },
}))

const renderFile = (fileUrl: string): React.ReactNode => {
  const theme = useTheme()
  const split = fileUrl.split('/')
  const fileName = split.pop() || ''
  const extension = fileName.split('.').pop()?.toLowerCase()

  let icon: React.ReactElement

  switch (extension) {
    case 'pdf':
      icon = <PictureAsPdfIcon sx={{ color: '#f44336', fontSize: 28 }} />
      break
    case 'doc':
    case 'docx':
      icon = <DescriptionIcon sx={{ color: '#2196f3', fontSize: 28 }} />
      break
    case 'xls':
    case 'xlsx':
      icon = <TableChartIcon sx={{ color: '#4caf50', fontSize: 28 }} />
      break
    case 'ppt':
    case 'pptx':
      icon = <SlideshowIcon sx={{ color: '#ff9800', fontSize: 28 }} />
      break
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      icon = <ImageIcon sx={{ color: '#9c27b0', fontSize: 28 }} />
      break
    default:
      icon = (
        <InsertDriveFileIcon
          sx={{ color: theme.palette.text.secondary, fontSize: 28 }}
        />
      )
      break
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {icon}
      <Typography
        variant="body2"
        sx={{
          ml: 1.5,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: 500,
        }}
      >
        {fileName}
      </Typography>
    </Box>
  )
}

type Props = {
  fileUrl?: string
  onChange: (fileUrl: string) => void
  multiple?: boolean
  label?: string
  buttonLabel?: string
  accept?: string
}

const UploadFile: React.FC<Props> = ({
  fileUrl,
  onChange,
  label,
  buttonLabel,
  accept,
}) => {
  const theme = useTheme()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [uploadedKbytes, setUploadedKbytes] = useState(0)
  const [isUploading, setisUploading] = useState(false)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length) {
      const f = files[0]
      setFile(f)
      onChange(URL.createObjectURL(files[0]))
    }
  }

  const uploadFile = (file: File) => {
    setisUploading(true)
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${process.env.REACT_APP_API_URL}/static`, true)
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    xhr.setRequestHeader(
      'Authorization',
      'Bearer ' + localStorage.getItem('authToken'),
    )
    xhr.setRequestHeader('x-file-name', encodeURI(file.name))
    xhr.responseType = 'json'

    xhr.setRequestHeader('x-file-type', FileTypeEnum.PRESCRIPTION)
    xhr.upload.onprogress = (e) => {
      const uploadedKB = Math.floor(e.loaded / 1024)
      if (uploadedKB % 10 === 0) {
        setUploadedKbytes(uploadedKB)
      }
    }
    xhr.onload = () => {
      if (xhr.status === 201) {
        const data = xhr.response
        onChange(data.fileUrl)
      }
      setisUploading(false)
    }
    xhr.send(file)
  }

  useEffect(() => {
    if (file) {
      uploadFile(file)
    }
  }, [file])

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {file && isUploading ? (
          <Box sx={{ mr: 2 }}>
            <CircularProgressWithLabel
              value={Math.round(
                (uploadedKbytes / Math.floor(file.size / 1024)) * 100,
              )}
              size={40}
            />
          </Box>
        ) : null}

        <Button
          component="label"
          variant="contained"
          color="primary"
          startIcon={<CloudUploadIcon />}
          disabled={isUploading}
          sx={{
            borderRadius: theme.shape.borderRadius * 1.5,
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: theme.shadows[1],
            padding: theme.spacing(1, 2),
            transition: theme.transitions.create(
              ['background-color', 'box-shadow', 'transform'],
              {
                duration: theme.transitions.duration.short,
              },
            ),
            '&:hover': {
              boxShadow: theme.shadows[3],
              transform: 'translateY(-2px)',
            },
          }}
        >
          {buttonLabel || 'Télécharger un fichier'}
          <input
            onChange={handleFileChange}
            style={{ display: 'none' }}
            type="file"
            ref={fileInputRef}
            accept={accept}
          />
        </Button>
      </Box>

      {fileUrl && (
        <Fade in={Boolean(fileUrl)}>
          <FilePreview elevation={0}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <a
                href={fileUrl}
                target="_blank"
                rel="noreferrer"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'flex',
                  flexGrow: 1,
                  overflow: 'hidden',
                }}
              >
                {renderFile(fileUrl)}
              </a>
              <IconButton
                size="small"
                onClick={() => onChange('')}
                sx={{
                  color: theme.palette.error.main,
                  ml: 1,
                  '&:hover': {
                    backgroundColor: theme.palette.error.light + '20',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </FilePreview>
        </Fade>
      )}
    </Box>
  )
}

export default UploadFile
